import { allWorkspacesStateSingleton, V4UUID } from '@/firebase/database';
import { Message } from '@/modules/nodesCanvasModule/components/storeClasses/message';
import { Node } from '@/modules/nodesCanvasModule/components/storeClasses/node';
import { Service } from '@/modules/nodesCanvasModule/components/storeClasses/service';
import { Topic } from '@/modules/nodesCanvasModule/components/storeClasses/topic';
import { primitiveMessages } from '@/modules/nodesCanvasModule/store';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';

interface ItemDependencyInfo {
  packageId: V4UUID;
  packageName: string;
}
export interface MsgDependencyInfo extends ItemDependencyInfo {
  msg: Message;
}

export interface TopicDependencyInfo extends ItemDependencyInfo {
  topic: Topic;
}

export interface NodeDependencyInfo extends ItemDependencyInfo {
  node: Node;
}

export interface ServiceDependencyInfo extends ItemDependencyInfo {
  srv: Service;
}
export interface NodeServiceDependencies {
  servers: ServiceDependencyInfo[]; // services that have intended node as server
  clients: ServiceDependencyInfo[]; // services that have intended node as client
}

export const getMsgDependency = (
  workspaceId: V4UUID,
  msgId: V4UUID
): MsgDependencyInfo | undefined => {
  if (primitiveMessages.find((msg) => msg.id === msgId)) {
    return {
      packageId: 'std_msgs',
      packageName: 'std_msgs',
      msg: primitiveMessages.find((msg) => msg.id === msgId)!
    };
  }
  for (let pkg of Object.entries(
    allWorkspacesStateSingleton().info?.[workspaceId].packages
  ).map((pkgInfo) => pkgInfo[1] as PackageT)) {
    if (!pkg.msgs) continue;
    let msg = pkg.msgs.find((message) => message.id === msgId);
    if (msg)
      return {
        packageId: pkg.id,
        packageName: pkg.name,
        msg: msg
      };
  }
  return undefined;
};

export const getTopicDependency = (
  workspaceId: V4UUID,
  topicId: V4UUID
): TopicDependencyInfo | undefined => {
  for (let pkg of Object.entries(
    allWorkspacesStateSingleton().info?.[workspaceId].packages
  ).map((pkgInfo) => pkgInfo[1] as PackageT)) {
    if (!pkg.topics) continue;
    let topic = pkg.topics.find((topic) => topic.id === topicId);
    if (topic)
      return {
        packageId: pkg.id,
        packageName: pkg.name,
        topic: Topic.fromJSON(topic)
      };
  }
  return undefined;
};

export const getNodeDependency = (
  workspaceId: V4UUID,
  nodeId: V4UUID
): NodeDependencyInfo | undefined => {
  for (let pkg of Object.entries(
    allWorkspacesStateSingleton().info?.[workspaceId].packages
  ).map((pkgInfo) => pkgInfo[1] as PackageT)) {
    if (!pkg.nodes) continue;
    let node = pkg.nodes.find((node) => node.id === nodeId);
    if (node)
      return {
        packageId: pkg.id,
        packageName: pkg.name,
        node: node
      };
  }
  return undefined;
};

export const getServiceDependency = (
  workspaceId: V4UUID,
  serviceId: V4UUID
): ServiceDependencyInfo | undefined => {
  for (let pkg of Object.entries(
    allWorkspacesStateSingleton().info?.[workspaceId].packages
  ).map((pkgInfo) => pkgInfo[1] as PackageT)) {
    if (!pkg.services) continue;
    let srv = pkg.services.find((srv) => srv.id === serviceId);
    if (srv)
      return {
        packageId: pkg.id,
        packageName: pkg.name,
        srv: srv
      };
  }
  return undefined;
};

/**
 * Gets all services that depend on a given node
 * @param workspaceId
 * @param nodeId - id of node to find service dependencies of
 * @returns
 */
export const getNodeServiceDependencies = (
  workspaceId: V4UUID,
  nodeId: V4UUID
): NodeServiceDependencies => {
  let nodeServiceDependencies: NodeServiceDependencies = {
    servers: [],
    clients: []
  };

  for (let pkg of Object.entries(
    allWorkspacesStateSingleton().info?.[workspaceId].packages
  ).map((pkgInfo) => pkgInfo[1] as PackageT)) {
    if (!pkg.services) continue;
    pkg.services.forEach((srv: Service) => {
      let serviceInfo: ServiceDependencyInfo = {
        packageId: pkg.id,
        packageName: pkg.name,
        srv: srv
      };
      if (srv.server === nodeId) {
        nodeServiceDependencies.servers.push(serviceInfo);
      }
      if (srv.clients && srv.clients.includes(nodeId)) {
        nodeServiceDependencies.clients.push(serviceInfo);
      }
    });
  }
  return nodeServiceDependencies;
};

/**
 * Gets a list of the names of the package dependencies of a node.
 * for use in generating the packagexml and cmakelist files
 * @param node
 * @param workspaceId
 * @returns
 */
export const getNodeDependencies = (
  node: Node,
  workspaceId: V4UUID
): Array<string> => {
  const dependencies: string[] = [];
  // getting publisher and subscriber dependencies
  node.subscribers.concat(node.publishers).forEach((topicId) => {
    let topicInfo = getTopicDependency(workspaceId, topicId);
    if (!topicInfo) return;
    const msgInfo = getMsgDependency(workspaceId, topicInfo?.topic.msgType);
    if (!msgInfo) return;
    if (!dependencies.includes(msgInfo.packageName))
      dependencies.push(msgInfo.packageName);
  });

  // getting service dependencies
  let serviceDeps = getNodeServiceDependencies(workspaceId, node.id);
  serviceDeps.clients
    .concat(serviceDeps.servers)
    .forEach(
      (srvInfo) =>
        !dependencies.includes(srvInfo.packageName) &&
        dependencies.push(srvInfo.packageName)
    );

  // getting general ROS dependencies
  if (node.language === 'cpp') dependencies.push('rclcpp');
  else if (node.language === 'py') dependencies.push('rclpy');

  return dependencies;
};

/**
 * Goes through each node in the package and gets each external dependency
 * TODO: get srv and msg dependencies too
 * @param pkg
 * @param workspaceId
 * @returns
 */
export const getPackageDependencies = (
  pkg: PackageT,
  workspaceId: V4UUID
): Array<string> => {
  let pkgDeps: string[] = [];
  for (let node of pkg.nodes) {
    pkgDeps = pkgDeps.concat(getNodeDependencies(node, workspaceId));
  }
  pkgDeps = pkgDeps.filter((depPkg) => depPkg !== pkg.name);
  return [...new Set(pkgDeps)];
};
